/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { combineLatest, from, Observable, of, throwError } from 'rxjs';
import {
  filter,
  map,
  mergeAll,
  mergeMap,
  take,
  shareReplay,
  switchMap,
  toArray,
} from 'rxjs/operators';
import { Memoize } from 'typescript-memoize';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { BaseMetaState } from '@core/ngrx-legacy';
import { createActionResponseFactory } from '@core/ngrx-legacy';
import { ReportsGridItem } from '@core/reports-contracts';
import {
  ReportsComponentsActions,
  ReportsComponentsSelectors,
  ReportsDashboardsActions,
  ReportsDashboardsSelectors,
  ReportsPermissionsSelectors,
  ReportsSelectors,
} from '@core/reports-data';
import {
  ReportDashboardComponent,
  ReportDashboardComponentOrigin,
  ReportParamFilters,
  REPORTS_DEFAULT_VISIBILITY,
  ReportComponentGridPropsDecorated,
  ReportDashboard,
  ReportDashboardApiEntity,
  ReportsDashboardCreate,
  OptionalId,
  ReportDashboardGridComponent,
} from '@core/reports-types';
import type { AnySymbol } from '@core/symbol-legacy';
import { NotificationsController } from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';
import type { Visibility } from '@core/visibility-types';

import { beforeComponentCreate } from '../helpers/facade-helpers';

@Injectable({
  providedIn: 'root',
})
export class ReportDashboardsFacade {
  protected readonly store = inject(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly i18n = inject(I18nService);
  protected readonly notificationsCtrl = inject(NotificationsController);

  public activeDashboard$: Observable<ReportDashboard> = this.store.pipe(
    select(ReportsDashboardsSelectors.activeWithReferences),
    filterBoolean,
  );

  public activeDashboardMeta$: Observable<BaseMetaState> = this.store.pipe(
    select(ReportsDashboardsSelectors.activeMeta),
  );

  public dashboardsList$: Observable<ReportDashboard[]> = this.store.pipe(
    select(ReportsDashboardsSelectors.dashboardsListSorted),
    shareReplay(1),
  );

  public canManageReportDashboards$: Observable<boolean> = this.store.pipe(
    select(ReportsPermissionsSelectors.abilities),
    map(abilities => abilities?.canManageReportDashboards ?? false),
    shareReplay(1),
  );

  public dashboardsListMeta$: Observable<BaseMetaState> = this.store.pipe(
    select(ReportsDashboardsSelectors.meta),
    shareReplay(1),
  );

  protected onDashboardEditResponse$: Observable<ReportDashboardApiEntity | { error: any }> =
    this.actions$.pipe(
      ofType(
        ReportsDashboardsActions.createOneOptimisticSuccess,
        ReportsDashboardsActions.duplicateOneSuccess,
        ReportsDashboardsActions.createOneOptimisticFailed,
        ReportsDashboardsActions.duplicateOneFailed,
      ),
      map(action => ('error' in action.payload ? action.payload : action.payload.data)),
    );

  protected actionResponse = createActionResponseFactory(ReportsDashboardsActions);

  /**
   * Loads dashboards from SharedAnalytics backend
   */
  public loadDashboards(): void {
    this.store.dispatch(ReportsDashboardsActions.fetchCollection());
  }

  /**
   *
   * @param id - if not specified, active dashboard id is used
   */
  @Memoize()
  public getDashboard(id?: number | string): Observable<ReportDashboard> {
    if (!id) {
      return this.activeDashboard$;
    }
    return this.store.pipe(
      select(ReportsDashboardsSelectors.byId(id)),
      filterBoolean,
      shareReplay(1),
    );
  }

  @Memoize()
  public getDashboardWithReferences(id: number): Observable<ReportDashboard | null> {
    return this.store.pipe(
      select(ReportsDashboardsSelectors.dashboardWithReferencesById(id)),
      shareReplay(1),
    );
  }

  @Memoize()
  public getDashboardOrThrow(id: number | string): Observable<ReportDashboard> {
    const loaded$ = this.store.pipe(
      select(ReportsDashboardsSelectors.meta),
      filter(meta => meta.loaded),
      take(1),
    );

    return loaded$.pipe(
      switchMap(() => this.store.pipe(select(ReportsDashboardsSelectors.byId(id)))),
      map(dashboard =>
        dashboard ? of(dashboard) : throwError(() => new Error(`Dashboard not found`)),
      ),
      mergeAll(),
      shareReplay(1),
    );
  }

  @Memoize()
  public getDashboardComponents(dashboardId: number): Observable<ReportDashboardComponent[]> {
    return this.store.pipe(
      select(ReportsSelectors.selectDashboardComponents(dashboardId)),
      filterBoolean,
      map(({ components }) => components),
      shareReplay(1),
    );
  }

  @Memoize()
  public getDashboardMeta(id: number): Observable<BaseMetaState> {
    return this.store.pipe(
      select(ReportsDashboardsSelectors.entityMetaMap),
      map(metaMap => metaMap[id] ?? { pending: false, loaded: false, failed: false, error: null }),
    );
  }

  public createEmptyDashboard(): void {
    this.createDashboard(this.i18n.translate('tlr.new_board'), REPORTS_DEFAULT_VISIBILITY);
  }

  public createDashboard(name: string, visibility: Visibility): void {
    this.actionResponse(
      ReportsDashboardsActions.createOneOptimistic,
      {
        payload: {
          correlationId: generateUuid(),
          data: {
            name,
            visibility,
            origin: 'blank',
          },
        },
      },
      {},
    ).subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.notificationsCtrl.success(this.i18n.translate('tlr.toast_notifications.board_added'));
      }
    });
  }

  public duplicateDashboard(id: number): void {
    combineLatest([this.getDashboardComponents(id), this.getDashboard(id)])
      .pipe(take(1))
      .subscribe(([components, dashboard]) => {
        this.duplicateDashboardInternal({ ...dashboard, components });
      });
  }

  /**
   * @internal
   */
  public duplicateDashboardInternal(
    dashboard: OptionalId<Omit<ReportDashboard, 'components'>> & {
      components: Array<OptionalId<ReportDashboardComponent>>;
    },
    isFromTemplate = false,
  ): void {
    const origin = isFromTemplate ? 'template' : 'duplicate';

    this.actionResponse(ReportsDashboardsActions.duplicateOne, {
      payload: {
        data: {
          ...this.beforeDashboardCreate(dashboard, origin),
          name: isFromTemplate
            ? dashboard.name
            : `${dashboard.name} (${this.i18n.translate('app.common.copy')})`,
          origin,
        },
        correlationId: generateUuid(),
      },
    }).subscribe(({ isSuccess }) => {
      if (isSuccess) {
        const msg =
          origin === 'template'
            ? 'tlr.toast_notifications.board_added'
            : 'tlr.toast_notifications.board_duplicated';

        this.notificationsCtrl.success(this.i18n.translate(msg));
      }
    });
  }

  public deleteDashboard(id: number): void {
    this.getDashboard(id)
      .pipe(
        take(1),
        switchMap(dashboard =>
          this.actionResponse(ReportsDashboardsActions.removeOneOptimistic, {
            payload: { dashboard },
          }),
        ),
      )
      .subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.notificationsCtrl.success(
            this.i18n.translate('tlr.toast_notifications.board_deleted'),
          );
        }
      });
  }

  public updateDashboardVisibility(id: number, visibility: Visibility) {
    this.store.dispatch(
      ReportsDashboardsActions.updateVisibility({
        payload: {
          data: {
            id,
            visibility,
          },
        },
      }),
    );
  }

  public updateDashboardsPosition(dashboards: Array<{ id: number | string }>) {
    this.store.dispatch(
      ReportsDashboardsActions.updatePositionsOptimistic({
        payload: {
          correlationId: generateUuid(),
          data: dashboards.map((d, i) => ({ id: Number(d.id), position: i + 1 })),
        },
      }),
    );
  }

  public updateDashboardNameAndSymbol(id: number, name: string, symbol: AnySymbol | null): void {
    this.store.dispatch(
      ReportsDashboardsActions.updateNameAndSymbol({
        payload: {
          data: {
            id,
            name,
            symbol,
          },
        },
      }),
    );
  }

  public updateDashboardFilters(
    kind: 'date' | 'filters' | 'reset',
    id: number,
    filters: ReportParamFilters,
    dateFilter: Partial<ReportDashboard['dateFilter']>,
    local: boolean = true,
  ): void {
    const data = {
      id,
      filters,
      dateFilter: <any>{
        ...(dateFilter.dateRange ? { dateRange: dateFilter.dateRange } : {}),
        ...(dateFilter.dateStart ? { dateStart: dateFilter.dateStart } : {}),
        ...(dateFilter.dateEnd ? { dateEnd: dateFilter.dateEnd } : {}),
      },
    };

    const action = local
      ? ReportsDashboardsActions.updateFiltersLocal({ payload: { data, kind } })
      : ReportsDashboardsActions.updateFiltersOptimistic({
          payload: { data, correlationId: generateUuid(), kind },
        });

    this.store.dispatch(action);
  }

  public updateDashboardComponentsGridProps(dashboardId: number, data: ReportsGridItem[]): void {
    this.store.dispatch(
      ReportsComponentsActions.updateGridPropsOptimistic({
        payload: {
          correlationId: generateUuid(),
          data: {
            dashboardId,
            components: data.map(({ id, ...grid }) => ({
              id: Number(id),
              grid,
            })),
          },
        },
      }),
    );
  }

  public getDashboardCreateResponse(): Observable<ReportDashboardApiEntity | { error: any }> {
    return this.onDashboardEditResponse$;
  }
  public getDashboardEditResponse(): Observable<ReportDashboardApiEntity | { error: any }> {
    return this.onDashboardEditResponse$;
  }

  /**
   *
   * @param id - if not specified, active dashboard id is used
   * @returns
   */
  @Memoize()
  public getDashboardGridComponents(id?: number): Observable<ReportDashboardGridComponent[]> {
    const componentIds$ = this.getDashboard(id).pipe(
      take(1),
      switchMap(x => from(x.componentIds)),
    );

    return componentIds$.pipe(
      mergeMap(componentId =>
        this.getComponentGridProps(componentId).pipe(
          take(1),
          map(gridProps => ({ $item: gridProps, notPlaced: false })),
        ),
      ),
      toArray(),
    );
  }

  @Memoize()
  public getComponentGridProps(id: number | string): Observable<
    ReportComponentGridPropsDecorated & {
      localId: string;
      refComponentId: number | string;
    }
  > {
    return this.store.pipe(
      select(ReportsComponentsSelectors.componentGridProps(id)),
      filterBoolean,
      shareReplay(1),
    );
  }

  protected beforeDashboardCreate(
    dashboard: OptionalId<
      ReportDashboard & {
        components: Array<OptionalId<ReportDashboardComponent>>;
      }
    >,
    origin: ReportDashboardComponentOrigin,
  ): ReportsDashboardCreate {
    const { id, ...result } = dashboard;

    return {
      ...result,
      components: result.components.map(c =>
        beforeComponentCreate(
          {
            ...c,
            origin,
          },
          generateUuid(),
          {
            resolveGridPosition: false,
          },
        ),
      ),
    };
  }
}
